/**
 * Influencer API client for Indian Coder Club.
 *
 * Thin wrappers around `/api/icc/influencer/*` endpoints. Mirrors the pattern
 * used by src/lib/api.ts — API responses are envelope `{success, data, message}`.
 *
 * Notable behavior:
 *  - `getMyProfile()` returns `null` when the user has no influencer record
 *    (backend responds 404/403). This lets pages distinguish "not an
 *    influencer" from "transport error".
 *  - All other calls surface errors normally via thrown Errors.
 */

import { getApiBaseUrl, PROJECT_KEY } from '../api'

const BASE = getApiBaseUrl()

interface Envelope<T> {
  success: boolean
  statusCode: number
  message: string
  data: T
}

function authHeaders(extra: Record<string, string> = {}): Record<string, string> {
  const token = localStorage.getItem('accessToken')
  return {
    'Content-Type': 'application/json',
    'X-Project-Key': PROJECT_KEY,
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
    ...extra,
  }
}

async function request<T>(endpoint: string, opts: RequestInit = {}): Promise<T> {
  const res = await fetch(`${BASE}${endpoint}`, { ...opts, headers: authHeaders(opts.headers as Record<string, string>) })

  if (res.status === 401) {
    localStorage.removeItem('accessToken')
    window.location.href = '/login'
    throw new Error('Session expired')
  }

  const json = (await res.json().catch(() => null)) as Envelope<T> | null
  if (!res.ok || !json || !json.success) {
    throw new Error(json?.message || `Request failed: ${res.status}`)
  }
  return json.data
}

// ── Domain types ──────────────────────────────────────────────────────────

export type InfluencerStatus = 'active' | 'paused' | 'suspended' | 'pending'
export type InfluencerTier = 'starter' | 'rising' | 'pro' | 'elite'
export type ConversionStatus = 'pending' | 'approved' | 'paid' | 'refunded' | 'cancelled'
export type PayoutMethod = 'upi' | 'bank' | 'razorpay'
export type PayoutStatus = 'pending' | 'processing' | 'paid' | 'failed'

export interface InfluencerProfile {
  id: string
  userId: string
  name: string
  email: string
  phone?: string | null
  tier: InfluencerTier
  status: InfluencerStatus
  // Commercial
  defaultCommissionPct: number
  maxCapInr: number
  defaultStudentDiscountPct: number
  holdingPeriodDays: number
  minimumPayoutInr: number
  // Payout details (editable)
  upiId?: string | null
  bankAccountHolderName?: string | null
  bankAccountNumber?: string | null
  ifsc?: string | null
  pan?: string | null
  gstRegistered?: boolean
  gstNumber?: string | null
  // Social
  instagramUrl?: string | null
  youtubeUrl?: string | null
  twitterUrl?: string | null
  linkedinUrl?: string | null
  // Meta
  createdAt: string
  updatedAt?: string | null
}

export interface UpdateProfilePayload {
  phone?: string | null
  upiId?: string | null
  bankAccountHolderName?: string | null
  bankAccountNumber?: string | null
  ifsc?: string | null
  pan?: string | null
  gstRegistered?: boolean
  gstNumber?: string | null
  instagramUrl?: string | null
  youtubeUrl?: string | null
  twitterUrl?: string | null
  linkedinUrl?: string | null
}

export interface DashboardSummary {
  lifetimeEarningsInr: number
  pendingBalanceInr: number
  holdingBalanceInr: number
  nextPayoutDate?: string | null
  avgHoldingReleaseDays?: number | null
  totalConversions: number
  conversionsByStatus: Record<ConversionStatus, number>
  recentConversions: Conversion[]
}

export interface InfluencerCode {
  id: string
  code: string
  slug: string
  label?: string | null
  courseSlug?: string | null
  courseTitle?: string | null
  commissionPct: number
  maxCapInr: number
  studentDiscountPct: number
  validFrom?: string | null
  validUntil?: string | null
  status: 'active' | 'paused' | 'expired'
  usageCount: number
  totalEarningsInr: number
  createdAt: string
}

export interface Conversion {
  id: string
  createdAt: string
  studentDisplayName: string
  courseSlug: string
  courseTitle: string
  code: string
  orderAmountInr: number
  commissionInr: number
  status: ConversionStatus
  holdingReleaseAt?: string | null
}

export interface ConversionsQuery {
  status?: ConversionStatus | 'all'
  code?: string
  from?: string
  to?: string
  page?: number
  size?: number
}

export interface ConversionsResponse {
  items: Conversion[]
  total: number
  page: number
  size: number
}

export interface Payout {
  id: string
  periodFrom: string
  periodTo: string
  grossInr: number
  tdsInr: number
  netPaidInr: number
  method: PayoutMethod
  reference?: string | null
  paidAt?: string | null
  status: PayoutStatus
  conversionIds: string[]
}

export interface PayoutsQuery {
  page?: number
  size?: number
}

export interface PayoutsResponse {
  items: Payout[]
  total: number
  page: number
  size: number
}

export interface EarningsChartPoint {
  date: string // YYYY-MM-DD
  earningsInr: number
  conversions: number
}

// ── API surface ───────────────────────────────────────────────────────────

async function getMyProfile(): Promise<InfluencerProfile | null> {
  try {
    return await request<InfluencerProfile>('/api/icc/influencer/profile')
  } catch (e) {
    const msg = (e as Error).message || ''
    // Backend returns 404 ("not found") or 403 ("not an influencer") for
    // regular users. Treat both as "not registered" rather than a hard error.
    if (/not found|not registered|not an influencer|forbidden/i.test(msg)) return null
    // Also catch raw HTTP 404/403 messages like "Request failed: 404"
    if (/\b(404|403)\b/.test(msg)) return null
    throw e
  }
}

function updateMyProfile(data: UpdateProfilePayload): Promise<InfluencerProfile> {
  return request<InfluencerProfile>('/api/icc/influencer/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  })
}

function getDashboardSummary(): Promise<DashboardSummary> {
  return request<DashboardSummary>('/api/icc/influencer/dashboard/summary')
}

function getMyCodes(): Promise<InfluencerCode[]> {
  return request<InfluencerCode[]>('/api/icc/influencer/codes')
}

function getMyConversions(params: ConversionsQuery = {}): Promise<ConversionsResponse> {
  const q = new URLSearchParams()
  if (params.status && params.status !== 'all') q.set('status', params.status)
  if (params.code) q.set('code', params.code)
  if (params.from) q.set('from', params.from)
  if (params.to) q.set('to', params.to)
  if (params.page !== undefined) q.set('page', String(params.page))
  if (params.size !== undefined) q.set('size', String(params.size))
  const qs = q.toString()
  return request<ConversionsResponse>(`/api/icc/influencer/conversions${qs ? `?${qs}` : ''}`)
}

function getMyPayouts(params: PayoutsQuery = {}): Promise<PayoutsResponse> {
  const q = new URLSearchParams()
  if (params.page !== undefined) q.set('page', String(params.page))
  if (params.size !== undefined) q.set('size', String(params.size))
  const qs = q.toString()
  return request<PayoutsResponse>(`/api/icc/influencer/payouts${qs ? `?${qs}` : ''}`)
}

function getEarningsChart(days: 7 | 30 | 90): Promise<EarningsChartPoint[]> {
  return request<EarningsChartPoint[]>(`/api/icc/influencer/dashboard/earnings-chart?days=${days}`)
}

export const influencerApi = {
  getMyProfile,
  updateMyProfile,
  getDashboardSummary,
  getMyCodes,
  getMyConversions,
  getMyPayouts,
  getEarningsChart,
}

export default influencerApi
